import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FormProvider, useController, useFieldArray, useFormState, useWatch } from 'react-hook-form';
import classNames from 'classnames';
import { localChartPlaceholder } from 'redux/UserPreferences';
import { ExploreIcon, MoreIcon, BarsSolidIcon, RevertIcon } from 'components/icons';
import IconButton from 'components/shared/IconButton';
import UpdateButton from 'components/shared/UpdateButton';
import { TimeSelect } from 'components/shared/TimeSelect';
import { PerfectScrollbar } from 'components/shared/ReactPerfectScrollbar';
import ConsoleTile from 'components/shared/ConsoleTile/ConsoleTile';
import { useChartSetForm, useChartSet } from '../../hooks';
import { ChartSetPreferenceDropdown } from '../ChartSetPreferenceDropdown';
import { DeviceChartCard, DeviceChartPlaceholderCard } from '../DeviceChartCard';
import styles from './DeviceChartConsole.module.css';

const DeviceChartSetContext = createContext({ register: () => {} });

function DeviceChartSet({
  deviceId,
  preference,
  enabledZoom,
  enabledMarker,
  displayAxisTitle,
  displayActionAnnotation,
  displayUserAnnotation,
  displayDataLabel,
  displayMarker,
  onSelect,
  onUpdate,
  onManage,
  onCreate,
  onEditChart,
  onDeleteChart,
  disableChartExplorer
}) {
  const context = useChartSet({
    enabledZoom,
    enabledMarker,
    displayAxisTitle,
    displayActionAnnotation,
    displayUserAnnotation,
    displayDataLabel,
    displayMarker
  });
  const [methods, { handleCreate, handleUpdate }] = useChartSetForm({
    preference,
    deviceId,
    onCreate,
    onUpdate
  });

  return (
    <DeviceChartSetProvider {...context}>
      <FormProvider {...methods}>
        <DeviceChartSetConsole
          header={
            <DeviceChartSetHeader
              preference={preference}
              onCreate={handleCreate}
              onUpdate={handleUpdate}
              onSelect={onSelect}
              onManage={onManage}
              controls={
                disableChartExplorer ? (
                  <IconButton
                    size="small"
                    className={styles.disabledChartExplorer}
                    aria-label="Explore"
                    data-tip="Chart Explorer Unavailable"
                  >
                    <ExploreIcon size="medium" />
                  </IconButton>
                ) : (
                  <IconButton
                    size="small"
                    className={styles.chartExplorer}
                    component={Link}
                    to={`/console/chart-explorer/detail${deviceId ? `?deviceId=${deviceId}` : ''}`}
                    aria-label="Explore"
                    data-tip="View Chart Explorer"
                  >
                    <ExploreIcon size="medium" />
                  </IconButton>
                )
              }
            />
          }
        >
          <DeviceChartSetBody onEdit={onEditChart} onDelete={onDeleteChart} />
        </DeviceChartSetConsole>
      </FormProvider>
    </DeviceChartSetProvider>
  );
}

function DeviceChartSetProvider({ children, ...rest }) {
  return <DeviceChartSetContext.Provider value={rest}>{children}</DeviceChartSetContext.Provider>;
}

DeviceChartSet.Provider = DeviceChartSetProvider;

function useDeviceChartSetContext() {
  const context = useContext(DeviceChartSetContext);

  if (!context) {
    throw new Error('No context');
  }

  return context;
}

DeviceChartSet.useContext = useDeviceChartSetContext;

function DeviceChartSetConsole({ className, children, defaultHeight = 368, resizable = true, header, ...rest }) {
  return (
    <ConsoleTile
      tileHeader={header}
      customClass={classNames(styles.console, className)}
      defaultHeight={defaultHeight}
      resizable={resizable}
      {...rest}
    >
      {children}
    </ConsoleTile>
  );
}

DeviceChartSet.Console = DeviceChartSetConsole;

function DeviceChartSetHeader({ preference, onCreate, onUpdate, onSelect, onManage, disabled = false, controls }) {
  const [timeHistory, setTimeHistory] = useState([]);
  const { isSubmitting, isDirty } = useFormState();
  const title = useWatch({ name: 'name' });
  const local = useWatch({ name: 'local', defaultValue: false });
  const {
    field: { value: time, onChange: onTimeChange },
    fieldState: { isDirty: isTimeDirty }
  } = useController({ name: 'state.query.time' });

  const handleTimeChange = useCallback(
    (value) => {
      setTimeHistory([]);
      onTimeChange(value);
    },
    [onTimeChange]
  );

  useEffect(() => {
    setTimeHistory((history) => (isTimeDirty ? [...history, time] : [time]));
  }, [isTimeDirty, time]);

  return (
    <div className={styles.header}>
      {!disabled ? (
        <ChartSetPreferenceDropdown preference={preference} onSave={onCreate} onSelect={onSelect} onManage={onManage} />
      ) : (
        <IconButton variant="text" size="small" className={styles.moreButton} data-tip="Please select a device">
          <MoreIcon size="medium" className={styles.more} />
        </IconButton>
      )}
      <div className={styles.title} data-testid="chart-set-name">
        {title}
      </div>
      <UpdateButton className={styles.update} updating={isSubmitting} disabled={local || !isDirty} onClick={onUpdate}>
        Update
      </UpdateButton>
      <div className={styles.controls}>
        {controls}
        {!disabled && (
          <div className={classNames(styles.control, { [styles.disabled]: isSubmitting })}>
            <IconButton
              size="small"
              aria-label="Reset time range"
              className={styles.resetTime}
              disabled={timeHistory.length < 2}
              onClick={() => handleTimeChange(timeHistory[0])}
            >
              <RevertIcon size="medium" />
            </IconButton>
            <TimeSelect value={time} onChange={handleTimeChange} direction="left" />
          </div>
        )}
      </div>
    </div>
  );
}

DeviceChartSet.Header = DeviceChartSetHeader;

function DeviceChartSetBody({ classes, onEdit, onDelete, disabled = false }) {
  const query = useWatch({ name: 'state.query' });
  const { fields: charts } = useFieldArray({ name: 'state.charts' });

  return (
    <div className={styles.container}>
      <PerfectScrollbar>
        <div className={classNames(styles.row, classes?.row)}>
          {charts.map((chart, index) => (
            <DeviceChartSetChart
              key={chart.id}
              name={`state.charts.${index}`}
              query={query}
              classes={classes}
              onEdit={onEdit}
              onDelete={onDelete}
              disabled={disabled}
            />
          ))}
        </div>
      </PerfectScrollbar>
    </div>
  );
}

DeviceChartSet.Body = DeviceChartSetBody;

function DeviceChartSetChart({ name, query, classes, onEdit, onDelete, disabled = false }) {
  const chartRef = useRef(null);
  const chart = chartRef.current;
  const { register, resetMarker, cancelMarker, config } = useDeviceChartSetContext();
  const {
    field: { value: meta, onChange }
  } = useController({ name });
  const handleEdit = useCallback(() => {
    onEdit({
      meta,
      query,
      onChange
    });
  }, [onEdit, onChange, meta, query]);
  const handleDelete = useCallback(() => {
    onDelete({
      ...meta,
      onDelete: () => {
        onChange({
          preferenceId: localChartPlaceholder.preferenceId,
          local: true
        });
      }
    });
  }, [onDelete, onChange, meta]);

  useEffect(() => {
    if (!chart) {
      return () => {};
    }

    return register(chart);
  }, [chart, register]);

  if (disabled) {
    return (
      <div className={classNames(styles.item_placeholder, classes?.item_placeholder)}>
        <BarsSolidIcon className={styles.item_placeholder_icon} />
        <span className={styles.item_placeholder_text}>Search for a device to display chart</span>
      </div>
    );
  }

  const itemProps = {
    name,
    classes,
    onEdit: handleEdit,
    onChange
  };

  return (
    <div className={classNames(styles.item, classes?.item)}>
      {meta.preferenceId !== localChartPlaceholder.preferenceId ? (
        <DeviceChartCard
          ref={chartRef}
          meta={meta}
          query={query}
          {...itemProps}
          {...config}
          onDelete={handleDelete}
          onMarkerPlaceEnd={resetMarker}
          onMarkerPlaceCancel={cancelMarker}
        />
      ) : (
        <DeviceChartPlaceholderCard {...itemProps} />
      )}
    </div>
  );
}

export default DeviceChartSet;
